import React, { memo } from 'react';
import type { WorkCard } from './workLayout';

interface VideoViewProps {
  /** 「加载更多」展开态：false 时底部渐变示意还有更多内容（裁剪窗口由 WorksSection 控制） */
  expanded: boolean;
  /** v1.4.108 已放置卡片（含实际渲染 x/y/w/h），由 WorksSection 实测列宽断点后统一布局计算 */
  cards: (WorkCard & { x: number; y: number; w: number; h: number })[];
  /** 画布总高（随列宽/用户作品动态变化；无作品 4 列 = 设计稿 1398 等比映射） */
  canvasH: number;
  /** 点击卡片 → 预览视频作品页（202:501），由 WorksSection → HomePage → AppNew 冒泡 */
  onOpen?: (card: WorkCard) => void;
}

/**
 * 视频作品网格（设计稿 183:179）——即梦式流式版
 * - 列数/列宽由 WorksSection 实测容器宽断点计算，卡片直接以 x/y/w/h 绝对定位渲染
 *   （无 1400 定宽画布、无 transform 缩放）；卡高 = 封面（192×colW/340）+ 固定 78px 文字区
 * - 卡片内部坐标全部由封面高推导（封面底 +16 标题 / +42 头像与胶囊 / +48 用户名），
 *   文字字号恒定不随列宽缩放（340 基准：标题 14px Medium / 用户名 12px #8BCDFF）
 * - 卡片数据由 WorksSection 计算（无作品 4 列 = 设计稿 20 卡行序构图；其余行序重排，
 *   用户作品前插）
 * - 带 videoUrl 的卡片用 <video preload=metadata> 静音展示首帧（无封面图时浏览器自动取首帧兜底）
 * - 悬停态（141:241）：文字区底渐变 wrapper 透明度 0.3→1；信息行右侧浮现「查看创作流程」
 *   胶囊（24 高与头像行对齐，右缘内缩 12，无填充、描边 #363636、圆角 8，文字 12px Regular）；
 *   showFlow !== false 才渲染——公开制作流程的用户作品 / 模板卡显示，私密的用户作品不显示
 * - 底部渐变遮罩：贴裁剪窗口底（1260 − 240 = top 1020；画布更矮时贴画布底），
 *   峰值 0.4，展开后隐藏
 */

const DEFAULT_AVATAR = '/images/home/avatars/avatar-user.png';

const VideoViewComponent: React.FC<VideoViewProps> = ({ expanded, cards, canvasH, onOpen }) => {
  return (
    /* 流式画布：高度即布局总高，卡片按实测坐标绝对定位，无缩放层 */
    <div className="relative w-full overflow-hidden" style={{ height: canvasH }}>
      {cards.map((card) => {
        // 封面高 = 卡高 − 固定 78px 文字区（WorksSection 换算：192×colW/340 + 78）
        const coverH = card.h - 78;
        return (
          <figure
            key={card.key}
            className="group absolute cursor-pointer"
            style={{ left: card.x, top: card.y, width: card.w, height: card.h }}
            onClick={() => onOpen?.(card)}
          >
            {/* 底层渐变 wrapper：90deg #141414→#282828，radius 12；悬停透明度 0.3→1（141:241） */}
            <div
              className="absolute inset-0 opacity-30 transition-opacity duration-200 group-hover:opacity-100"
              style={{
                background: 'linear-gradient(90deg, rgba(20, 20, 20, 1) 0%, rgba(40, 40, 40, 1) 100%)',
                borderRadius: 12,
              }}
            />

            {/* 封面：宽 = 列宽、高 = 卡高 − 78，radius 16；视频卡用 video 静音展示首帧；
                裁剪容器轮廓固定，悬停时内部媒体放大被边缘裁切 */}
            <div
              className="absolute left-0 top-0 overflow-hidden rounded-2xl"
              style={{ width: card.w, height: coverH }}
            >
              {card.videoUrl ? (
                <video
                  src={card.videoUrl}
                  poster={card.src || undefined}
                  muted
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
                />
              ) : (
                <img
                  src={card.src}
                  alt={card.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
                />
              )}
            </div>

            {/* 标题：封面底 16，宽留出右侧胶囊位（340 基准 198 → 卡宽 − 142） */}
            <p
              className="absolute left-3 truncate text-[14px] font-medium leading-[14px] text-white"
              style={{ top: coverH + 16, width: card.w - 142 }}
            >
              {card.title}
            </p>

            {/* 头像 24x24（封面底 42）+ 用户名（封面底 48）12px #8BCDFF */}
            <img
              src={card.avatar || DEFAULT_AVATAR}
              alt=""
              className="absolute left-3 h-6 w-6 rounded-full object-cover"
              style={{ top: coverH + 42 }}
            />
            <span
              className="absolute left-11 text-[12px] leading-[12px] text-[#8BCDFF]"
              style={{ top: coverH + 48 }}
            >
              {card.user}
            </span>

            {/* 悬停态：信息行右侧「查看创作流程」胶囊（141:241：24 高与头像行对齐，右缘内缩 12，
                无填充、描边 #363636、圆角 8；文字 12px Regular，边框盒内垂直水平居中） */}
            {card.showFlow !== false && (
              <span
                className="pointer-events-none absolute opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                style={{
                  right: 12,
                  top: coverH + 42,
                  height: 24,
                  lineHeight: '22px',
                  padding: '0 11px',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  color: '#FFFFFF',
                  border: '1px solid #363636',
                  borderRadius: 8,
                }}
              >
                查看创作流程
              </span>
            )}
          </figure>
        );
      })}

      {/* 底部渐变遮罩：相对裁剪窗口定位（1020 + 240 = 1260 窗口底），画布被压短时贴画布底；
          峰值 0.4（用户要求更透明），展开后隐藏 */}
      {!expanded && (
        <div
          className="pointer-events-none absolute left-0 w-full"
          style={{
            top: Math.min(1020, Math.max(0, canvasH - 240)),
            height: 240,
            background: 'linear-gradient(180deg, rgba(20, 20, 20, 0) 0%, rgba(20, 20, 20, 0.4) 100%)',
          }}
        />
      )}
    </div>
  );
};

export const VideoView = memo(VideoViewComponent);
